import {
  Button,
  Classes,
  ControlGroup,
  FormGroup,
  H5, Icon,
  InputGroup,
  Intent, Label,
  MenuItem,
  Overlay, Position,
  Spinner, Tooltip,
  RadioGroup, Radio
} from "@blueprintjs/core";
import React, {useContext, useState, useEffect} from "react";
import {MultiSelect} from "@blueprintjs/select";
import Service from "../Service";
import AppContext from "../AppContext";

const epListName = 'Endpoints & SaaS Apps'
const hybridListName = 'Hybrid Workloads'
const RegionsMultiSelect = MultiSelect.ofType();
var enableDebugging

function debug(debugMessage) {
  if(!enableDebugging) {
    return
  }

  console.log(debugMessage)
}

function makeID(name) {
  return name.replace(/\s/g, '-').replace(/\W/g, '').toLowerCase()
}

function _get(source, path) {
  return path.split('.').reduce((target, each) => {
    return target[each]
  }, source)
}

function FieldLabelWithInfo({label, tooltip = [
  'Select the storage region or regions that best suits your business requirements.'
]}) {
  return (
    <ControlGroup vertical={false} className="field-label-with-info">
      <Label>{label}</Label>
      {tooltip && (
        <Tooltip content={
          <div>
            {tooltip.map(each => <div>{each}</div>)}
          </div>
        } position={Position.TOP}>
          <Icon icon="info-sign" />
        </Tooltip>
      )}
    </ControlGroup>
  )
}

function CreditsDistribution({listName, types, credits, disabled = false, onChange = ()=> {}, showMode = 'remaining'}) {
  const [consumptions, setConsumptions] = useState({})
  const [maxConsumptions, setMaxConsumptions] = useState(0)
  const [totalConsumed, setTotalConsumed] = useState(0)
  const [remainingConsumed, setRemainingConsumed] = useState(0)

  useEffect(() => {
    const total = Object.values(consumptions).reduce((total, each) => total + each, 0)
    setTotalConsumed(total)
    setRemainingConsumed(credits - total)
  }, [consumptions])

  useEffect(() => {
    onChange({
      listName,
      consumptions,
      totalConsumed
    })
  }, [totalConsumed])

  useEffect(() => {
    setConsumptions(types.reduce((acc, t) => {
      acc[t] = 0
      return acc
    }, {}))
  }, [])

  return listName && types?.length && credits > 0 && (
    <section className="credits-distribution-wrap">
      <div className="bp3-callout">
        Thank you for your purchase that includes <strong>{credits}</strong> user licenses for SaaS Apps.
        <br/>
        Please tell us how you would like to utilize these user licenses.
      </div>
      <div className="apps-list">
        {types.map((each, key) => (
            <FormGroup
                key={key}
                helperText=""
                label={`${each}`}
                labelFor={`${makeID(each)}-licenses`}
            >
              <InputGroup id={makeID(each)} value={''}
                          disabled={disabled}
                          onChange={(e) => {
                            setConsumptions({
                              ...consumptions,
                              [each]: Number(e.target.value)
                            })
                          }}
                          value={consumptions[each]}
                          placeholder="" />
            </FormGroup>
        ))}
      </div>
      {
        showMode === 'remaining' && remainingConsumed !== 0 ?
          <small className="danger-text">
            Remaining Licenses <strong>{remainingConsumed}</strong>.
            <br/>Utilized licenses do not equal the total purchased.
          </small> :
        showMode === 'total' && remainingConsumed !== 0 ?
          <small className="danger-text">
            Total Licenses <strong>{totalConsumed}</strong>.
            <br/>Utilized licenses do not equal the total purchased.
          </small> : null
      }

    </section>
  )
}

function validateCloudStorage(formData, formFields, invitation) {
  // This logic is used for the situation where a product is not enabled for Azure
  // so I force it to aws since aws is our primary cloud provider and always has been
  var hasEPWorkloads = invitation.products && invitation.products.find(e => e === epListName) ? true : false
  var hasHybridWorkloads = invitation.products && invitation.products.find(e => e === hybridListName) ? true : false
  
  debug(`OpenState - hasEPWorkloads: ${hasEPWorkloads}`)
  debug(`OpenState - hasHybridWorkloads: ${hasHybridWorkloads}`)

  if(hasEPWorkloads) {
    debug('OpenState - FOUND EP PRODUCT')

    formData['cloudStorage'][epListName] = 'aws'          
    
    debug(`OpenState - formData: ${JSON.stringify(formData)}`)

    if(!formData?.awsRegions[epListName] || (formData?.awsRegions[epListName] && formData?.awsRegions[epListName].length === 0)) {
      formFields.push(`awsRegions[${epListName}]:AWS ${epListName} Storage Region:required`)
    }    
  }
  
  if (hasHybridWorkloads) {
    debug('OpenState - FOUND HYBRID PRODUCT')

    var hasAWSRegions = invitation.awsProductRegions && invitation.awsProductRegions[hybridListName].length > 0 ? true : false
    var hasAzureRegions = invitation.azureProductRegions && invitation.azureProductRegions[hybridListName].length > 0 ? true : false

    debug(`OpenState - Has AWS Regions?: ${hasAWSRegions}`)
    debug(`OpenState - Has Azure Regions?: ${hasAzureRegions}`)

    if(hasAWSRegions && !hasAzureRegions) {
      formData['cloudStorage'][hybridListName] = 'aws'
    } else if(!hasAWSRegions && hasAzureRegions) {
      formData['cloudStorage'][hybridListName] = 'azure'
    } 

    debug(`OpenState - Cloud Storage: ${formData['cloudStorage'][hybridListName]}`)

    if(formData.cloudStorage[hybridListName]) {
      var cloudStorage = formData.cloudStorage[hybridListName]
      
      if(cloudStorage === 'aws' && hasAWSRegions && formData?.awsRegions[hybridListName].length == 0) {
        debug('OpenState - Has AWS regions')

        formFields.push(`awsRegions[${hybridListName}]:AWS ${hybridListName} Storage Region:required`)
      } 
      
      if(cloudStorage === 'azure' && hasAzureRegions && formData?.azureRegions[hybridListName].length == 0) {
        debug('OpenState - Has Azure regions')

        formFields.push(`azureRegions[${hybridListName}]:Azure ${hybridListName} Storage Region:required`)
      } 
      
      if(cloudStorage === 'awsAndAzure') {
        if(hasAWSRegions && formData?.awsRegions[hybridListName].length == 0) {
          formFields.push(`awsRegions[${hybridListName}]:AWS ${hybridListName} Storage Region:required`)
        }

        if(hasAzureRegions && formData?.azureRegions[hybridListName].length == 0) {
          formFields.push(`azureRegions[${hybridListName}]:Azure ${hybridListName} Storage Region:required`)
        }
  
        debug('OpenState - Expecting both region types (1)')
      }      
    } 
    else {
      formFields.push(`awsRegions[${hybridListName}]:AWS ${hybridListName} Storage Region:required`)
      formFields.push(`azureRegions[${hybridListName}]:Azure ${hybridListName} Storage Region:required`)

      debug('OpenState - Expecting both region types (2)')
    }
  } 
}

function OpenState(props) {
  debug(`OpenState - props: ${JSON.stringify(props)}`)

  const appCtx = useContext(AppContext)

  const [submitting, setSubmitting] = useState(false)
  const [canSubmit, setCanSubmit] = useState(false)

  const invitation = props?.invitation
  const regionTypes = invitation?.products
  const saasLicenses = invitation?.saasLicenses
  const publicDomains = invitation?.publicDomains || []
  enableDebugging = props?.debugMode ? props.debugMode : false

  debug(`OpenState - invitation: ${JSON.stringify(invitation)}`)
  debug(`OpenState - regionTypes: ${regionTypes}`)  

  const formFields = [
    'firstName:First Name:required',
    'lastName:Last Name:required',    
    'adminEmail:Email Address:required,email,noPublicEmailDomains',        
    // ...regionTypes.map(t => `regions.${t}:${t}:required`),
    ...Object.keys(saasLicenses)
        .filter(t => saasLicenses[t].credits > 0)
        .map(t => `productDistribution.${t}:${t}:required,creditsAccountedFor`)
  ]  

  debug(`OpenState - formFields: ${formFields}`)

  const Validators = {
    required: {
      message() { return `Please enter some information in the field.` },
      validate(val) {
        if (val instanceof Array) {
          return !!val.length
        } else if (typeof val === 'object') {
          return !!Object.keys(val).length
        } else if (val && val.trim) {
          return !!val.trim().length
        } else {
          return !!val
        }
      }
    },
    chooseOne: {
      message() { return `Please choose one or more from the list.` },
      validate(val) {
        if (val instanceof Array) {
          return !!val.length
        } else if (typeof val === 'object') {
          return !!Object.keys(val).length
        } else if (val && val.trim) {
          return !!val.trim().length
        } else {
          return !!val
        }
      }
    },
    email: {
      message() { return `Please enter a valid email address.` },
      validate(val) { return /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i.test(val) },
    },
    noPublicEmailDomains: {
      message() { return `Please enter a work email address - not from public email domains.` },
      validate(val) {
        return publicDomains.filter(d => {
          return new RegExp(`\\@${d}$`).test(val)
        }).length === 0
      }
    },
    creditsAccountedFor: {
      message() { return false },
      validate(val, fieldName) {
        const data = saasLicenses[fieldName.split('.').pop()]
        if (Object.keys(val).length !== data.types.length) {
          return false
        }
        const totalConsumption = Object.values(val).reduce((total, each) => total + each, 0)
        if (totalConsumption !== data.credits) {
          return false
        }
        return true
      }
    }
  }

  const [formData, setFormData] = useState({
    productDistribution: Object.keys(saasLicenses).reduce((acc, each) => {
      acc[each] = {}
      return acc
    }, {}),
    awsRegions: regionTypes.reduce((acc, each) => {
      acc[each] = []
      return acc
    }, {}),
    azureRegions: regionTypes.reduce((acc, each) => {
      acc[each] = []
      return acc
    }, {}),
    cloudStorage: regionTypes.reduce((acc, each) => {
      acc[each] = ''
      return acc
    }, {})
  })

  const [dirtyState, setDirtyState] = useState({})
  const [validState, setValidState] = useState({})

  validateCloudStorage(formData, formFields, invitation)

  useEffect(() => {
    setValidState({})
    validateForm()    
  }, [formData])

  function validateForm() {
    let isValid, messaging = {}
    try {
      const r = formFields.map(fld => {
        const [fieldName, fieldLabel, validators] = fld.split(':')
        if (validators.length) {
          const vals = validators.split(',')
          return vals.reduce((acc, vName) => {
            const validator = Validators[vName]
            const result = validator.validate(_get(formData, fieldName), fieldName, formData)
            if (result) {
              acc++
              if (!messaging[fieldName]) {
                messaging = {
                  ...messaging,
                  [fieldName]: null
                }
              }
            } else if (!messaging[fieldName] && validator.message()) {
              messaging = {
                ...messaging,
                [fieldName]: validator.message()
              }
            }
            return acc
          }, 0) === vals.length
        }
        return true
      })
      isValid = r.filter(valid => !!valid).length === formFields.length
    } catch(e) {
      isValid = false
    }

    debug(`validateForm - messaging: ${JSON.stringify(messaging)}`)

    setValidState(messaging)
    setCanSubmit(isValid)

    return isValid
  }

  function handleCreditsDistributionChange({listName, consumptions, totalConsumed}) {
    if (listName && consumptions) {
      setFormData({
        ...formData,
        productDistribution: {
          ...formData.productDistribution,
          [listName]: consumptions
        }
      })
    }
  }

  function CloudStorage({formData, listName}) {
    debug(`handleCloudStorageChange - listName: ${JSON.stringify(listName)}`)

    const handleCloudStorageChange = (e) => {
      var cloudStorage = e.currentTarget.value

      debug(`handleCloudStorageChange - cloudStorage for ${listName}: ${cloudStorage}`)

      if (listName && cloudStorage) {
        debug(`handleCloudStorageChange - INSIDE listName: ${JSON.stringify(listName)}`)        

        setFormData({
          ...formData,
          cloudStorage: {
            ...formData.cloudStorage,
            [listName]: cloudStorage
          }
        })        

        debug(`handleCloudStorageChange - formFields: ${JSON.stringify(formFields)}`)

        setDirtyState({...dirtyState, [`cloudStorage[${listName}]`] : true})
      }
    }    

    validateCloudStorage(formData, formFields, invitation)

    // This clears the list
    if(formData.cloudStorage[listName] === 'aws' && formData.azureRegions[listName].length > 0) {
      formData.azureRegions[listName] = []
    } else if(formData.cloudStorage[listName] === 'azure' && formData.awsRegions[listName].length > 0) {
      formData.awsRegions[listName] = []
    }    

    debug(`handleCloudStorageChange - formData(2): ${JSON.stringify(formData)}`)
    
    return (     
      <>
      <FormGroup            
            helperText=""
            label={<FieldLabelWithInfo label="Cloud Storage"
                                        tooltip={[
                                          'Azure storage currently supports limited workloads — contact your account team for details.'
                                        ]} />}
            labelFor={`cloud-storage-${listName}`}
          >            
        <RadioGroup 
          inline="true" 
          onChange={handleCloudStorageChange}         
          selectedValue={formData?.cloudStorage[listName]}>
          <Radio name="aws" label="AWS" value={'aws'}/>
          <Radio name="azure" label="Azure" value={'azure'}/>
          <Radio name="awsAndAzure" label="AWS + Azure" value={'awsAndAzure'}/>
        </RadioGroup>
      </FormGroup>
      {       
        !formData?.cloudStorage[listName] ?
          <small className="danger-text">
            Please select a <strong>Cloud Storage</strong> option<br /><br />
          </small> : null
      }
      </> 
    )
  }

  function renderProductDetails(data, listName, key) {
    debug(`renderProductDetails - data: ${JSON.stringify(data)}`)

    const saasLicenses = data?.saasLicenses[listName]
    const hasAWSRegions = data?.awsProductRegions[listName] && data?.awsProductRegions[listName].length > 0 ? true : false
    const hasAzureRegions = data?.azureProductRegions[listName] && data?.azureProductRegions[listName].length > 0 ? true : false
    const showCloudProviderSelection = hasAWSRegions && hasAzureRegions ? true : false
    let showAWSRegions = false

    if((showCloudProviderSelection && formData?.cloudStorage[listName] !== '') || !showCloudProviderSelection) {      
      showAWSRegions = true

      // This handles setting the default when only have a single allowed value
      if(data?.awsProductRegions[listName] && data?.awsProductRegions[listName].length === 1) {
        formData.cloudStorage[listName] = 'aws'

        handleAddMultiSelectItem(data?.awsProductRegions[listName][0], listName, 'aws')
      }
    }
    
    // Assume AWS for now 
    const cloudStorage = formData?.cloudStorage[listName] ? formData?.cloudStorage[listName] : 'aws'

    return (
      <>
        {saasLicenses && (
            <CreditsDistribution
                listName={listName}
                onChange={handleCreditsDistributionChange}
                {...saasLicenses}
            />
        )}
       
        {showCloudProviderSelection && (
        <CloudStorage
          id={'cloudStorage-' + key}
          formData={formData}
          listName={listName}
        />
        )}
        
        {data?.awsProductRegions[listName] && hasAWSRegions && showAWSRegions && cloudStorage && cloudStorage.indexOf('aws') >= 0 && (
          <FormGroup            
            key={'aws-' + key}
            helperText=""
            label={<FieldLabelWithInfo label={`AWS ${listName} Storage Region`} />}
            labelFor={`aws-${listName}-region`}
          >
          <RegionsMultiSelect
              id={`awsRegions[${listName}]`}
              fill={true} minimal={true}
              items={data.awsProductRegions[listName]}
              itemRenderer={(item, options) => renderMultiSelectMenuItem(item, options, listName, 'aws')}
              noResults={<MenuItem disabled={true} text="No regions found" />}
              selectedItems={formData.awsRegions[listName]}
              onItemSelect={(item) => handleAddMultiSelectItem(item, listName, 'aws')}
              popoverProps={{minimal: true}}
              tagRenderer={(tag) => renderMultiSelectTag(tag)}
              tagInputProps={{
                disabled: submitting,
                inputProps: {
                  readOnly: true,
                  onBlur: () => {                    
                    setDirtyState({...dirtyState, [`awsRegions[${listName}]`]: true})
                  },
                },
                onRemove: (item, index) => handleRemoveMultiSelectItem(index, listName, 'aws'),
                rightElement: formData.awsRegions[listName].length > 0 ?
                    <Button icon="cross" minimal={true} onClick={() => clearMultiSelectList(listName, 'aws')} /> :
                    undefined,
                tagProps: {minimal: true,},
              }}
              placeholder=""
          />
          {Messaging(`awsRegions[${listName}]`)}
        </FormGroup>
        )}
        {data?.azureProductRegions[listName] && hasAzureRegions && formData?.cloudStorage[listName] && (formData.cloudStorage[listName].indexOf('azure') >= 0 || formData.cloudStorage[listName].indexOf('Azure') >= 0) && (
          <FormGroup            
            key={'azure-' + key}
            helperText=""
            label={<FieldLabelWithInfo label={`Azure ${listName} Storage Region`} />}
            labelFor={`azure-${listName}-region`}
        >
          <RegionsMultiSelect
              id={`azureRegions[${listName}]`}
              fill={true} minimal={true}
              items={data.azureProductRegions[listName]}
              itemRenderer={(item, options) => renderMultiSelectMenuItem(item, options, listName, 'azure')}
              noResults={<MenuItem disabled={true} text="No regions found" />}
              selectedItems={formData.azureRegions[listName]}
              onItemSelect={(item) => handleAddMultiSelectItem(item, listName, 'azure')}
              popoverProps={{minimal: true}}
              tagRenderer={(tag) => renderMultiSelectTag(tag)}
              tagInputProps={{
                disabled: submitting,
                inputProps: {
                  readOnly: true,
                  onBlur: () => {                    
                    setDirtyState({...dirtyState, [`azureRegions[${listName}]`]: true})
                  },
                },
                onRemove: (item, index) => handleRemoveMultiSelectItem(index, listName, 'azure'),
                rightElement: formData.azureRegions[listName].length > 0 ?
                    <Button icon="cross" minimal={true} onClick={() => clearMultiSelectList(listName, 'azure')} /> :
                    undefined,
                tagProps: {minimal: true,},
              }}
              placeholder=""
          />
          {Messaging(`azureRegions[${listName}]`)}
        </FormGroup>
        )}        
      </>
    )
  }

  function renderMultiSelectMenuItem(item, {handleClick}, listName, cloudProvider) {
    // debug(`renderMultiSelectMenuItem - item: ${JSON.stringify(item)}`)
    // debug(`renderMultiSelectMenuItem - handleClick: ${handleClick}`)

    let isSelected
    
    if(cloudProvider === 'aws') {
      isSelected = formData.awsRegions[listName].find(e => e.name === item.name)
    } else if(cloudProvider === 'azure') {
      isSelected = formData.azureRegions[listName].find(e => e.name === item.name)
    }

    return <MenuItem key={item.name}
                     icon={isSelected ? "tick" : "blank"}
                     text={item.displayName}
                     shouldDismissPopover={false}
                     onClick={handleClick} />
  }

  function renderMultiSelectTag(item) {
    return `${item.displayName}`
  }

  function handleAddMultiSelectItem(item, listName, cloudProvider) {
    if(cloudProvider === 'aws') {
      if(!formData.awsRegions[listName].find(e => e.name === item.name)) {
        setFormData({
          ...formData,
          awsRegions: {
            ...formData.awsRegions,
            [listName]: [
              ...formData.awsRegions[listName],
              item
            ]
          }

        })
      }
    } else if(cloudProvider === 'azure') {
      if(!formData.azureRegions[listName].find(e => e.name === item.name)) {
        setFormData({
          ...formData,
          azureRegions: {
            ...formData.azureRegions,
            [listName]: [
              ...formData.azureRegions[listName],
              item
            ]
          }

        })
      }
    }
  }

  function handleRemoveMultiSelectItem(index, listName, cloudProvider) {
    if(cloudProvider === 'aws') {
      const updated = [
        ...formData.awsRegions[listName].slice(0, index),
        ...formData.awsRegions[listName].slice(index + 1),
      ]
      setFormData({
        ...formData,
        awsRegions: {
          ...formData.awsRegions,
          [listName]: updated
        }
      })
    } else if(cloudProvider === 'azure') {
      const updated = [
        ...formData.azureRegions[listName].slice(0, index),
        ...formData.azureRegions[listName].slice(index + 1),
      ]
      setFormData({
        ...formData,
        azureRegions: {
          ...formData.azureRegions,
          [listName]: updated
        }
      })
    }    
  }

  function clearMultiSelectList(listName, cloudProvider) {
    debug(`clearMultiSelectList - listName: ${JSON.stringify(listName)}`)
    debug(`clearMultiSelectList - cloudProvider: ${cloudProvider}`)

    if(cloudProvider === 'aws') {
      setFormData({
        ...formData,
        awsRegions: {
          ...formData.awsRegions,
          [listName]: []
        }
  
      })
    } else if(cloudProvider === 'azure') {
      setFormData({
        ...formData,
        azureRegions: {
          ...formData.azureRegions,
          [listName]: []
        }
  
      })
    }    
  }

  async function handleFormSubmit() {
    debug(`handleFormSubmit - formFields: ${JSON.stringify(formFields)}`)

    if (!canSubmit) {
      const dirty = {}
      formFields.forEach(fld => {
        const [fieldName, validators] = fld.split(':')
        dirty[fieldName] = true
      })
      setDirtyState(dirty)
      return
    }

    const productDetails = regionTypes.reduce((acc, product) => {
      const ret = {
        product,
        awsRegions: formData.awsRegions[product].map(region => ({name: region.name})),
        azureRegions: formData.azureRegions[product].map(region => ({name: region.name}))
      }

      if (formData.productDistribution[product]) {
        ret.productDistribution = Object.keys(formData.productDistribution[product]).map(each => ({
          name: each,
          quantity: formData.productDistribution[product][each]
        }))
      }
      acc.push(ret)
      return acc
    }, [])

    const {
      firstName, lastName, adminEmail,
      cloudStorage
    } = formData


    const toSubmit = {
      orderNumber: invitation.orderNumber,
      orderBusinessUnitId: invitation.orderBusinessUnitId,
      responseState: 'PROVIDED',
      responseDetails: {
        orderNumber: invitation.orderNumber,
        orderBusinessUnitId: invitation.orderBusinessUnitId,
        firstName, lastName, adminEmail,
        cloudStorage,
        productDetails
      }
    }

    setSubmitting(true)
    const result = await Service.submitOrderInfo(invitation.invitationId, toSubmit)
    setSubmitting(false)

    if (result?.isSuccess) {
      appCtx.setAppState({
        type: 'CONFIRM'
      })
    } else {
      appCtx.setAppState({
        type: 'ERROR',
        invitation: result
      })
    }
  }

  async function handleNotMe() {
    setSubmitting(true)
    const result = await Service.omitInvitation(invitation)
    setSubmitting(false)
    if (result?.isSuccess) {
      appCtx.setAppState({
        type: 'OMITTED',
        invitation: {}
      })
    } else {
      appCtx.setAppState({
        type: 'ERROR',
        invitation: result
      })
    }
  }

  function Messaging(fieldName) {
    return dirtyState[fieldName] && validState[fieldName] && (<small className="danger-text">{validState[fieldName]}</small>)
  }

  return (
      <section className="form-wrap">
        <h1>Let's get started with some basic information and your preferences</h1>
        <form className="bordered">
          <h4>Please provide the following information below</h4>
          <FormGroup
              helperText=""
              label="First Name"
              labelFor="first-name"
          >
            <InputGroup id="first-name" value={formData.firstName}
                        disabled={submitting}
                        onChange={(e) => {setFormData({...formData, firstName: e.target.value })}}
                        onBlur={() => setDirtyState({...dirtyState, firstName: true})}
                        placeholder="" />
            {Messaging('firstName')}
          </FormGroup>
          <FormGroup
              helperText=""
              label="Last Name"
              labelFor="last-name"
          >
            <InputGroup id="last-name" value={formData.lastName}
                        disabled={submitting}
                        onChange={(e) => {setFormData({...formData, lastName: e.target.value })}}
                        onBlur={() => setDirtyState({...dirtyState, lastName: true})}
                        placeholder="" />
            {Messaging('lastName')}
          </FormGroup>
          <FormGroup
              helperText=""
              label={<FieldLabelWithInfo label="Email Address"
                                         tooltip={[
                                          'Enter a valid corporate email address.',
                                          'A public domain email address like gmail.com is not accepted.'
                                        ]} />}
              labelFor="email-address"
          >
            <InputGroup id="email-address" value={formData.adminEmail}
                        disabled={submitting}
                        inputProps={{type: 'email'}}
                        onChange={(e) => {setFormData({...formData, adminEmail: e.target.value })}}
                        onBlur={() => setDirtyState({...dirtyState, adminEmail: true})}
                        placeholder="" />
            {Messaging('adminEmail')}
          </FormGroup>
          {
            regionTypes.map((type, i) =>
              renderProductDetails(props?.invitation, type, i)
            )
          }

          <small className="privacy-message disabled-text">
            <p>
              The requested data is necessary for the performance of the service you’ve ordered to
              <a href="https://i.dell.com/sites/doccontent/legal/terms-conditions/en/Documents/Dell-Technologies-List-for-Privacy-Statement-May-2018.pdf">Dell Technologies</a>.
              The data you’ve provided to us will be processed by Dell Technologies in accordance with our
              <a href="https://www.dell.com/learn/us/en/uscorp1/policies-privacy-country-specific-privacy-policy">Privacy Statement</a>.
            </p>
            <p>
              By clicking “Submit”, you have read our
              <a href="https://www.dell.com/learn/us/en/uscorp1/policies-privacy-country-specific-privacy-policy">Privacy Statement</a>,
              understood your rights and how
              <a href="https://i.dell.com/sites/doccontent/legal/terms-conditions/en/Documents/Dell-Technologies-List-for-Privacy-Statement-May-2018.pdf">Dell Technologies</a> uses and protects your data.
            </p>
          </small>

          <Button fill={true}
                  disabled={!canSubmit || submitting}
                  onClick={handleFormSubmit}
                  intent={Intent.PRIMARY}>
            Submit
          </Button>
          <div className="hr-with-text">
            <hr/>
            <label>OR</label>
          </div>

          <Button fill={true}
                  disabled={submitting}
                  intent={Intent.PRIMARY} outlined={true}
                  onClick={handleNotMe}>I do not have this information</Button>

          <Overlay className={Classes.OVERLAY_SCROLL_CONTAINER}
                   isOpen={submitting} usePortal={false}
                   canEscapeKeyClose={false} canOutsideClickClose={false}>
            <Spinner size={50} />
            <H5>Submitting...</H5>
          </Overlay>
        </form>

        <h6 className="disabled-text">If you have any questions or concerns, please reach out to <a href="http://support.dell.com">support.dell.com</a></h6>
      </section>
  )
}


export default OpenState
